import React from 'react';
import { Box, Typography, Paper, Chip } from '@mui/material';
import {
  AccountBalanceWallet as WalletIcon,
  FilterList as FilterIcon,
  TrendingUp as TrendingIcon,
  CheckCircle as CheckIcon,
  Person as PersonIcon,
} from '@mui/icons-material';
import { EducationModal, type EducationStep } from '../shared/EducationModal';

// Visual: Net balance calculation
const BalanceCalculationVisual: React.FC = () => (
  <Box sx={{ textAlign: 'center' }}>
    <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, alignItems: 'center', mb: 1 }}>
      <Paper elevation={0} sx={{ p: 1.5, bgcolor: 'error.50', border: 1, borderColor: 'error.200', minWidth: 80 }}>
        <Typography variant="caption" color="error.dark" display="block">Owes</Typography>
        <Typography variant="h6" color="error.main">$50</Typography>
      </Paper>
      <Typography variant="h5" color="text.secondary">-</Typography>
      <Paper elevation={0} sx={{ p: 1.5, bgcolor: 'success.50', border: 1, borderColor: 'success.200', minWidth: 80 }}>
        <Typography variant="caption" color="success.dark" display="block">Won</Typography>
        <Typography variant="h6" color="success.main">$200</Typography>
      </Paper>
      <Typography variant="h5" color="text.secondary">=</Typography>
      <Paper elevation={0} sx={{ p: 1.5, bgcolor: 'primary.50', border: 1, borderColor: 'primary.200', minWidth: 80 }}>
        <Typography variant="caption" color="primary.dark" display="block">Net</Typography>
        <Typography variant="h6" color="primary.main">+$150</Typography>
      </Paper>
    </Box>
    <Typography variant="caption" color="text.secondary">
      Net Balance = Winnings - Entry Fees
    </Typography>
  </Box>
);

// Visual: Filter chips
const FilterVisual: React.FC = () => (
  <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, flexWrap: 'wrap' }}>
    <Chip label="All" color="primary" size="small" />
    <Chip label="Owes Money" variant="outlined" size="small" sx={{ borderColor: 'error.main', color: 'error.main' }} />
    <Chip label="Owed Money" variant="outlined" size="small" sx={{ borderColor: 'success.main', color: 'success.main' }} />
    <Chip label="Settled" variant="outlined" size="small" icon={<CheckIcon sx={{ fontSize: 14 }} />} />
  </Box>
);

// Visual: Player types
const PlayerTypesVisual: React.FC = () => (
  <Box sx={{ display: 'flex', justifyContent: 'center', gap: 3 }}>
    <Box sx={{ textAlign: 'center' }}>
      <Box sx={{ width: 40, height: 40, borderRadius: '50%', bgcolor: 'primary.main', display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 0.5 }}>
        <PersonIcon sx={{ color: 'white', fontSize: 24 }} />
      </Box>
      <Typography variant="caption" display="block">Regular User</Typography>
      <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10 }}>Has account</Typography>
    </Box>
    <Box sx={{ textAlign: 'center' }}>
      <Box sx={{ width: 40, height: 40, borderRadius: '50%', bgcolor: 'grey.400', display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 0.5 }}>
        <PersonIcon sx={{ color: 'white', fontSize: 24 }} />
      </Box>
      <Typography variant="caption" display="block">Offline Player</Typography>
      <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10 }}>No account</Typography>
    </Box>
  </Box>
);

// Visual: Cash flow metrics
const CashFlowVisual: React.FC = () => (
  <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
    {[
      { label: '7 Days', in: '$120', out: '$80' },
      { label: '30 Days', in: '$450', out: '$300' },
      { label: 'All Time', in: '$2.1k', out: '$1.5k' },
    ].map((period) => (
      <Paper key={period.label} elevation={0} sx={{ p: 1, textAlign: 'center', bgcolor: 'grey.50', border: 1, borderColor: 'grey.200', minWidth: 70 }}>
        <Typography variant="caption" fontWeight="bold" display="block">{period.label}</Typography>
        <Typography variant="caption" color="success.main" display="block">+{period.in}</Typography>
        <Typography variant="caption" color="error.main" display="block">-{period.out}</Typography>
      </Paper>
    ))}
  </Box>
);

const steps: EducationStep[] = [
  {
    title: 'Welcome to Accounting',
    description: 'Track payments and winnings across all your completed boards. See who owes you money and who you need to pay out.',
    icon: <WalletIcon sx={{ fontSize: 40 }} />,
    visual: <BalanceCalculationVisual />,
  },
  {
    title: 'Filter Players',
    description: 'Quickly find players by their payment status. Filter to see who owes money, who is owed winnings, or whose accounts are fully settled.',
    icon: <FilterIcon sx={{ fontSize: 40 }} />,
    visual: <FilterVisual />,
  },
  {
    title: 'All Player Types',
    description: 'Track both regular users with accounts and offline players you created. Everyone\'s balances are managed in one place.',
    icon: <PersonIcon sx={{ fontSize: 40 }} />,
    visual: <PlayerTypesVisual />,
  },
  {
    title: 'Cash Flow Insights',
    description: 'View your money flow over time - see how much you\'ve collected from entries and paid out in winnings across different time periods.',
    icon: <TrendingIcon sx={{ fontSize: 40 }} />,
    visual: <CashFlowVisual />,
  },
];

interface AccountingEducationModalProps {
  open: boolean;
  onClose: () => void;
}

export const AccountingEducationModal: React.FC<AccountingEducationModalProps> = ({ open, onClose }) => (
  <EducationModal
    open={open}
    onClose={onClose}
    modalId="accounting"
    steps={steps}
    headerColor="primary.main"
    finishButtonText="View Accounting"
  />
);

export default AccountingEducationModal;
